// RENT AND FOOD ABOVE THE SCHOOL'S ALLOWANCE — every sentence, in ONE place
// (ZEN-818). The Overview, the All beneficiaries page and ZEN-835 read these;
// none of them writes its own. The figures come from the server's one reading
// (rent_and_food_above_status in backend/routes/dashboard.py).
//
// Maryse's wording, ZEN-818 session, 1 Nov 2026 ("1. agree"): the second
// sentence names the figure — "If this $1,400.00 is withdrawn from the 529
// account, …". Her rule for the term: the first time COA appears on a page
// it is written "cost of attendance (COA)".
import { money } from "@/lib/format";

const usd = (v) => money(v, "USD");
const coa = (first) => (first ? "cost of attendance (COA)" : "COA");

/** "$1,400.00 of this is above the school's COA allowance. If this $1,400.00
 * is withdrawn from the 529 account, the earnings portion may be subject to
 * income tax plus a 10% federal penalty." */
export function aboveAllowanceSentence(amount, { firstMention = false } = {}) {
  const w = pastAllowanceWords(amount, { firstMention });
  return `${w.note} If ${w.risk}`;
}

/** THE TWO HALVES, for the withdrawal form's red note and its tick box (and
 * the expense window's, ZEN-838). Maryse, ZEN-842, 3 Nov 2026: the note is red
 * ("I think this should be red when it is truly above") and Save works only
 * once the box is ticked; the box is her 1 Nov sentence in the family's own
 * voice.
 *   note: "$1,400.00 of this is above the school's COA allowance."
 *   tick: "I understand that if this $1,400.00 is withdrawn from the 529
 *          account, the earnings portion may be subject to income tax plus a
 *          10% federal penalty." */
export function pastAllowanceWords(amount, { firstMention = false } = {}) {
  const risk = `this ${usd(amount)} is withdrawn from the 529 account, the earnings portion may be subject to income tax plus a 10% federal penalty.`;
  return {
    note: `${usd(amount)} of this is above the school's ${coa(firstMention)} allowance.`,
    risk,
    tick: `I understand that if ${risk}`,
  };
}

/** Beside rent the automatic fill left out (ZEN-842, item 6). */
export const PAY_REST_OUTSIDE_LINK = "Open the expense to pay the rest outside the 529";

/** Yellow (situation A): not yet taken out of the 529. */
export function cautionWords(caution, { firstMention = false } = {}) {
  return {
    title: `Not settled yet: repay only ${usd(caution.safe)} from the 529.`,
    body: aboveAllowanceSentence(caution.amount, { firstMention }),
    short: `Not settled: repay only ${usd(caution.safe)} from the 529. ${usd(caution.amount)} is above the COA allowance.`,
  };
}

/** Red (situation D): already taken out, and the year is beyond its
 * qualified expenses. Maryse's wording, ZEN-818 session, 2 Nov 2026 (she
 * asked for a clearer version, then "I like your revised version" and,
 * on keeping the title, "agree"). Rent and food UP TO the allowance are
 * qualified, so the reason names only the part above it. */
export function overWords(over, year, { firstMention = false } = {}) {
  return {
    title: "Tell your tax accountant.",
    body: `529 withdrawals in ${year} are ${usd(over.amount)} more than the year's qualified expenses. ` +
      `Rent and food above the school's ${coa(firstMention)} allowance are not qualified expenses. ` +
      `The earnings portion of this ${usd(over.amount)} may be subject to income tax plus a 10% federal penalty.`,
    short: `${usd(over.amount)} more than the year's qualified expenses: rent and food above the COA allowance are not qualified. Tell your tax accountant.`,
  };
}

/** Situations B and C: one plain line, the amount lumped together. */
export const PAID_OUTSIDE_LABEL = "Paid outside the 529 account";
export const PAID_BY_529_LABEL = "Paid by the 529 account";
export const SEE_PRIVATE_SETTLEMENTS = "see Private Settlements";
